'use client';

import { useRef } from 'react';

import { Box, Container, Stack } from '@mui/material';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import { Mousewheel, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import CarouselButton from '@/components/CarouselButton';
import GlowingIcon from '@/components/GlowingIcon';
import SectionTitle from '@/components/SectionTitle';
import ChevronLeft from '@/components/SvgIcons/ChevronLeft';
import ChevronRight from '@/components/SvgIcons/ChevronRight';
import Like from '@/components/SvgIcons/Like';
import Quote from '@/components/SvgIcons/Quote';
import TestimonialCard from '@/components/TestimonialCard';
import { testimonials } from '@/config/testimonials.config';
import colors from '@/styles/themes/colors';
import { useGlowEffect } from '@/utils/hooks/useGlowEffect';

import styles from './TestimonialsSection.module.scss';

const TestimonialsSection = () => {
  const swiperRef = useRef<SwiperType | null>(null);
  const { ref, onMouseMove, onMouseLeave, style } = useGlowEffect({ color: colors.green500 });

  const handleSlideNext = () => {
    swiperRef.current?.slideNext();
  };

  const handleSlidePrev = () => {
    swiperRef.current?.slidePrev();
  };

  return (
    <Container component="section" maxWidth="xl" className={styles.container}>
      <Container
        ref={ref}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        maxWidth={false}
        disableGutters
        className={styles.content}
        style={style}
      >
        <Box className={styles.wrapper}>
          <SectionTitle
            translationNamespace="home.testimonialsSection"
            translationKey="title"
            icon={
              <GlowingIcon variant="green" className={styles.icon}>
                <Like size={18} />
              </GlowingIcon>
            }
            textAlign="center"
            variant="h4"
            component="h2"
          />
          <Box className={styles.sliderWrapper}>
            <CarouselButton
              onClick={handleSlidePrev}
              icon={ChevronLeft}
              style={styles.prevButton}
              ariaLabel="Previous testimonial"
            />
            <Swiper
              modules={[Navigation, Mousewheel]}
              speed={400}
              slidesPerView={1}
              spaceBetween={0}
              loop
              mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
              grabCursor
              onSwiper={swiper => {
                swiperRef.current = swiper;
              }}
              className={styles.swiper}
            >
              {testimonials.map(testimonial => (
                <SwiperSlide key={testimonial.id}>
                  <TestimonialCard testimonial={testimonial} />
                </SwiperSlide>
              ))}
              <Box className={styles.quote}>
                <Quote />
              </Box>
            </Swiper>
            <CarouselButton
              onClick={handleSlideNext}
              icon={ChevronRight}
              style={styles.nextButton}
              ariaLabel="Next testimonial"
            />
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              gap={2}
              mt={3}
              display={{ xs: 'flex', md: 'none' }}
            >
              <CarouselButton onClick={handleSlidePrev} icon={ChevronLeft} ariaLabel="Previous testimonial" />
              <CarouselButton onClick={handleSlideNext} icon={ChevronRight} ariaLabel="Next testimonial" />
            </Stack>
          </Box>
        </Box>
        <Box className={styles.ellipse} />
      </Container>
    </Container>
  );
};

export default TestimonialsSection;
